import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Typography,
  MenuItem,
  Grid,
  Avatar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UserIcon from '@mui/icons-material/Person';
import { useProjectStore } from '../store/projectStore';
import { useIssueStore } from '../store/issueStore';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { DeleteConfirmationDialog } from '../components/DeleteConfirmationDialog';
import { Project } from '../types/project';
import { format, getYear, subDays, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { projectUserApi } from '../api/projectUserApi';
import { userApi } from '../api/userApi';
import { User } from '../types/models';
import { useAuthStore } from '../store/authStore';
import { isAdmin } from '../utils/permissions';

type Order = 'asc' | 'desc';
type OrderBy = 'code' | 'name' | 'description' | 'created_on' | 'issues_count';

interface HeadCell {
  id: OrderBy | 'actions' | 'users';
  label: string;
  sortable: boolean;
}

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: OrderBy) => void;
  order: Order;
  orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const headCells: HeadCell[] = [
    { id: 'code', label: 'Event Code', sortable: true },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'description', label: 'Description', sortable: false },
    { id: 'created_on', label: 'Year Creation', sortable: true },
    { id: 'issues_count', label: 'No. Ebira issued', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false },
  ];

  const createSortHandler = (property: OrderBy) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id as OrderBy)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { projects, isLoading, error, fetchProjects, createProject, updateProject, deleteProject } = useProjectStore();
  const { issues, loading: issuesLoading, error: issuesError, fetchIssues } = useIssueStore();
  const { user } = useAuthStore(); // Get the current user from auth store

  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState<Project | undefined>(undefined);
  const [projectUsers, setProjectUsers] = React.useState<{[key: number]: User[]}>({});
  const [allUsers, setAllUsers] = React.useState<User[]>([]);
  
  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Sorting
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<OrderBy>('code');

  // Filtering
  const [filters, setFilters] = React.useState({
    search: '',
    year: 'all',
  });

  // Initial fetch
  React.useEffect(() => {
    fetchProjects();
    fetchIssues({});
    fetchAllUsers();
  }, [fetchProjects, fetchIssues]);

  // Fetch all users
  const fetchAllUsers = async () => {
    try {
      const users = await userApi.getUsers();
      // Filter out inactive users
      const activeUsers = users.filter(user => user.is_active);
      setAllUsers(activeUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Fetch users for projects
  React.useEffect(() => {
    const fetchUsersForProjects = async () => {
      const usersMap: {[key: number]: User[]} = {};
      
      for (const project of projects) {
        try {
          const userIds = await projectUserApi.getProjectUsers(project.id);
          usersMap[project.id] = userIds.map(userId => 
            allUsers.find(user => user.id === userId)
          ).filter(Boolean) as User[];
        } catch (error) {
          console.error(`Failed to fetch users for project ${project.id}:`, error);
          usersMap[project.id] = [];
        }
      }
      
      setProjectUsers(usersMap);
    };

    if (projects.length > 0 && allUsers.length > 0) {
      fetchUsersForProjects();
    }
  }, [projects, allUsers]);

  // Fetch when filters, pagination, or sorting change
  React.useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage, order, orderBy, filters, fetchProjects]);

  // Handle filter changes
  const handleFilterChange = (key: keyof typeof filters) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilters(prev => ({ ...prev, [key]: event.target.value }));
    setPage(0); // Reset to first page when filters change
  };

  // Get unique years from project creation dates
  const years = React.useMemo(() => {
    if (!Array.isArray(projects)) {
      console.error('Projects is not an array:', projects);
      return [];
    }
    const uniqueYears = new Set(
      projects.map(project => getYear(new Date(project.created_on)))
    );
    return Array.from(uniqueYears).sort((a, b) => b - a);
  }, [projects]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    setSelectedProject(undefined);
    setFormDialogOpen(true);
  };

  const handleEditClick = (project: Project) => {
    setSelectedProject(project);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (project: Project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (selectedProject) {
      await updateProject(selectedProject.id, data);
    } else {
      await createProject(data);
    }
    setFormDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (selectedProject) {
      await deleteProject(selectedProject.id);
      setSelectedProject(undefined);
      setDeleteDialogOpen(false);
    }
  };

  const handleUsersClick = () => {
    navigate(`/users`);
  };

  // Check if user has admin permissions
  const hasAdminPermission = isAdmin(user);
  
  // Filter and sort projects
  const filteredProjects = React.useMemo(() => {
    return projects
      .filter(project => {
        const matchesSearch = 
          project.code.toLowerCase().includes(filters.search.toLowerCase()) ||
          project.name.toLowerCase().includes(filters.search.toLowerCase());
        const matchesYear = filters.year === 'all' ? true : 
          getYear(new Date(project.created_on)) === parseInt(filters.year);
        return matchesSearch && matchesYear;
      })
      .sort((a, b) => {
        const isAsc = order === 'asc';
        switch (orderBy) {
          case 'code':
            return isAsc ? a.code.localeCompare(b.code) : b.code.localeCompare(a.code);
          case 'name':
            return isAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
          case 'description':
            return isAsc ? (a.description || '').localeCompare(b.description || '') : (b.description || '').localeCompare(a.description || '');
          case 'created_on':
            return isAsc ? 
              new Date(a.created_on).getTime() - new Date(b.created_on).getTime() :
              new Date(b.created_on).getTime() - new Date(a.created_on).getTime();
          case 'issues_count':
            return isAsc ? 
              issues.filter(issue => issue.project_id === a.id).length - issues.filter(issue => issue.project_id === b.id).length :
              issues.filter(issue => issue.project_id === b.id).length - issues.filter(issue => issue.project_id === a.id).length;
          default:
            return 0;
        }
      });
  }, [projects, filters, order, orderBy, issues]);

  // Pagination slice
  const paginatedProjects = filteredProjects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Custome styles
  const customTdWidth120 = { width: "100px"};

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs items={[{ label: 'Events' }]} />

      {/* Main Content */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Left Side */}
        <Grid item xs={12} sm={12}>
          {/* Left Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start'}}>
              <Typography variant="h5" component="h1">
                Events
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
              {hasAdminPermission && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<UserIcon />}
                    onClick={handleUsersClick}
                    sx={{ mr: 2 }}
                  >
                    Rule Setting
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                  >
                    Create Event
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                label="Sort By"
                variant="outlined"
                size="small"
                value={orderBy}
                onChange={(e) => {
                  setOrderBy(e.target.value as OrderBy);
                }}
              >
                <MenuItem value="code">Code</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                label="Order"
                variant="outlined"
                size="small"
                value={order}
                onChange={(e) => {
                  setOrder(e.target.value as Order);
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={filters.search}
                onChange={handleFilterChange('search')}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Year"
                variant="outlined"
                size="small"
                value={filters.year}
                onChange={handleFilterChange('year')}
              >
                <MenuItem value="all">All Years</MenuItem>
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Events Table */}
          <Paper>
            <TableContainer>
              <Table>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {paginatedProjects.map((project) => (
                    <TableRow
                      hover
                      key={project.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <TableCell>{project.code}</TableCell>
                      <TableCell>{project.name}</TableCell>
                      <TableCell>{project.description}</TableCell>
                      <TableCell>
                        {format(new Date(project.created_on), 'yyyy')}
                      </TableCell>
                      <TableCell>
                        {issues.filter(issue => issue.project_id === project.id).length}
                      </TableCell>
                      <TableCell style={customTdWidth120}>
                        {hasAdminPermission && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(project);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(project);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredProjects.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid> 
      </Grid>

      {/* Dialogs */}
      <ProjectFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        project={selectedProject}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Event"
        message={`Are you sure you want to delete project "${selectedProject?.name}"? This action cannot be undone.`}
      />
    </Box>
  );
};
